import { createContext, useContext, useEffect, useState } from "react";
import { apiLogin, apiVerifyLoginOtp } from "@services/api";
import { getUser, setUser, getIsAuth, setIsAuth, clearAuth } from "@utils/storage";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,            setUserState]    = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading,         setLoading]      = useState(true);

  // Restore session on mount
  useEffect(() => {
    if (getIsAuth()) {
      const u = getUser();
      if (u) { setUserState(u); setIsAuthenticated(true); }
    }
    setLoading(false);
  }, []);

  // Login — calls real API. Returns either a completed user object (no OTP
  // required - shouldn't normally happen now that OTP applies to all
  // roles, but kept as a safety net) or { requiresOtp: true, userSlug }
  // if a verification code was sent and needs to be confirmed via verifyOtp.
  const login = async (email, password) => {
    const data = await apiLogin(email, password);
    if (data?.status === 1 && data?.userDetails) {
      const u = data.userDetails;
      setUser(u);
      setIsAuth(true);
      setUserState(u);
      setIsAuthenticated(true);
      return u;
    }
    if (data?.status === 1 && data?.requires_otp) {
      return { requiresOtp: true, userSlug: data.user_slug };
    }
    throw new Error(data?.message || "Invalid credentials");
  };

  // Completes login after the OTP step - same state-setting logic login()
  // uses directly when no OTP step is needed.
  const verifyOtp = async (userSlug, otpCode) => {
    const data = await apiVerifyLoginOtp(userSlug, otpCode);
    if (data?.status === 1 && data?.userDetails) {
      const u = data.userDetails;
      setUser(u);
      setIsAuth(true);
      setUserState(u);
      setIsAuthenticated(true);
      return u;
    }
    throw new Error(data?.message || "Invalid verification code");
  };

  // Logout
  const logout = () => {
    clearAuth();
    setUserState(null);
    setIsAuthenticated(false);
  };

  // Handy slug getter
  const userSlug = user?.user_slug || user?.slug || null;

  if (loading) return null;

  return (
    <AuthContext.Provider value={{ user, userSlug, isAuthenticated, login, verifyOtp, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
export default AuthContext;
